import type { IAccount } from "@/modules/account/common/types";
import { CURRENCY } from "@/shared/common/constants";
import { Amount, Select } from "@/shared/components";
import { getTotalCommission, renderSelectedAccount } from "@/shared/helpers";
import { Divider } from "@ab.uitools/ui-library/components/Divider";
import { FormField } from "@ab.uitools/ui-library/components/FormField";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { useFormProps } from "@ab.uitools/ui-library/hooks/useFormProps";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { CommissionAccountSelectOption } from "./CommissionAccountSelectOption";

const DEFAULT_URGENT_COMMISSION = 10000;

interface Props {
  accountsList: IAccount[];
}

export const Commission = ({ accountsList }: Props) => {
  const { t } = useTranslation();
  const { watch, setValue } = useFormProps();
  const commissionData = watch?.("commissionData") || 0;
  const filteredAccountList = useMemo(() => {
    return accountsList.filter(({ currency }) => currency === CURRENCY.AMD.label)
      .map((item, index) => ({
        value: item.number,
        label: renderSelectedAccount(item),
        meta: { ...item, index },
      }));
  }, [accountsList]);

  const additionalCommission = commissionData.additionalCommission ? DEFAULT_URGENT_COMMISSION : 0;

  const totalCommission = useMemo(() => {
    return getTotalCommission(commissionData);
  }, [commissionData]);

  useEffect(() => {
    if (!totalCommission) {
      setValue?.("commissionAccount", "");
    }
  }, [totalCommission]);

  return (
    <div className="commission-container fs-14">
      {(!!commissionData.commission ||
        (!commissionData?.commission && !commissionData.additionalCommission)) && (
        <div className="mt-8">
          <div className="flexbox justify-content--between align-items--center pt-4 pb-12">
            <Text>{t("transfer.commissionFee")}</Text>
            <Amount
              className="fs-14"
              amount={commissionData.commission || 0}
              currency={CURRENCY.AMD.label}
            />
          </div>
        </div>
      )}
      {!!commissionData?.additionalCommission && (
        <div>
          {!!commissionData?.commission && (
            <Divider color="light" type="primary" isHorizontal/>
          )}
          <div className="flexbox justify-content--between align-items--center pt-4 pb-12 mt-8">
            <Text>{t("general.urgentCommissionFee")}</Text>
            <Amount
              className="fs-14"
              amount={additionalCommission}
              currency={CURRENCY.AMD.label}
            />
          </div>
        </div>
      )}
      {!!commissionData?.commission && !!commissionData.additionalCommission && (
        <div>
          <Divider color="light" type="primary" isHorizontal/>
          <div className="flexbox justify-content--between align-items--center pt-4 pb-12 mt-8">
            <Text weight="bold">
              {t("general.totalCommission")}
            </Text>
            <Amount
              className="fs-14"
              amount={totalCommission || 0}
              currency={CURRENCY.AMD.label}
            />
          </div>
        </div>
      )}
      {!isNaN(totalCommission) && !!totalCommission &&
        <FormField
          className="mt-12 mb-10"
          name="commissionAccount"
          errorMessageIcon="infoFilled"
          shouldValidate
          As={(props) => (
            <Select
              {...props}
              className="custom-select"
              options={filteredAccountList}
              placeholder={t("general.commissionAccountPlaceholder")}
              components={{ Option: CommissionAccountSelectOption }}
            />
          )}
        />
      }
    </div>
  );
};
